// Fallback API URL for production if environment variable is not set
const defaultApiUrl = process.env.NODE_ENV === 'production' 
  ? 'https://api.saillab.co.jp' 
  : 'http://localhost:5000';

const API_URL = `${process.env.REACT_APP_API_URL || defaultApiUrl}/api`;

// Helper function to get auth header
const getAuthHeader = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Generic fetch function with auth
const fetchWithAuth = async (endpoint: string, options: RequestInit = {}) => {
  // Start with required headers
  const baseHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  // Add auth header if token exists
  const authHeader = getAuthHeader();
  
  // Merge all headers
  const headers = {
    ...baseHeaders,
    ...authHeader,
    ...(options.headers as Record<string, string> || {})
  };

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include', // Add this to include cookies
  });

  if (response.status === 401) {
    // Token expired or invalid
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Authentication failed');
  }

  const data = await response.json();

  if (!response.ok) {
    // Try to extract detailed error information from server response
    let errorMessage = 'Something went wrong';
    
    if (data.error) {
      errorMessage = data.error;
    } else if (data.message) {
      errorMessage = data.message;
    } else if (data.details) {
      errorMessage = data.details;
    } else if (typeof data === 'string') {
      errorMessage = data;
    }
    
    // Add additional context for common HTTP status codes
    if (response.status === 400) {
      errorMessage = `Bad Request: ${errorMessage}`;
    } else if (response.status === 403) {
      errorMessage = `Access Denied: ${errorMessage}`;
    } else if (response.status === 404) {
      errorMessage = `Not Found: ${errorMessage}`;
    } else if (response.status >= 500) {
      errorMessage = `Server Error: ${errorMessage}`;
    }
    
    // Carry the HTTP status and any machine-readable `code` on the Error.
    // Callers that only read `.message` are unaffected, but a caller that needs
    // to tell "you sent something invalid" apart from "this is full, and only
    // someone else can fix it" can now do so without string-matching a message
    // that is also user-facing and translated.
    const error = new Error(errorMessage) as Error & {
      status?: number;
      code?: string;
      body?: unknown;
    };
    error.status = response.status;
    if (typeof data?.code === 'string') error.code = data.code;
    error.body = data;
    throw error;
  }

  return data;
};

// Export fetchWithAuth for use in other services
export { fetchWithAuth };

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    fetchWithAuth('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  register: (name: string, email: string, password: string) =>
    fetchWithAuth('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password }),
    }),

  logout: () => fetchWithAuth('/auth/logout', { method: 'POST' }),
};

// User API
export const userAPI = {
  getProfile: () => fetchWithAuth('/users/me'),

  updateProfile: (data: { name: string; timeZone: string }) =>
    fetchWithAuth('/users/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getAvailability: () => fetchWithAuth('/users/availability'),

  setAvailability: (data: {
    availabilitySchedule: Array<{
      days: number[];
      startTime: string;
      endTime: string;
    }>;
    timeZone: string;
  }) =>
    fetchWithAuth('/users/availability', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Meeting API
export const meetingAPI = {
  createMeeting: (data: any) =>
    fetchWithAuth('/meetings', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getUserMeetings: () => fetchWithAuth('/meetings/my'),

  getMeetingById: (id: string) => fetchWithAuth(`/meetings/${id}`),

  updateMeeting: (id: string, data: any) =>
    fetchWithAuth(`/meetings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteMeeting: (id: string) =>
    fetchWithAuth(`/meetings/${id}`, {
      method: 'DELETE',
    }),

  bookMeeting: (id: string, data: any) =>
    fetchWithAuth(`/meetings/${id}/book`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  cancelMeeting: (id: string, data: any) =>
    fetchWithAuth(`/meetings/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Generic API methods
const api = {
  auth: authAPI,
  user: userAPI,
  meeting: meetingAPI,

  // Generic HTTP methods for use by other services
  get: (endpoint: string) => fetchWithAuth(endpoint),

  post: (endpoint: string, data?: any) =>
    fetchWithAuth(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: (endpoint: string, data?: any) =>
    fetchWithAuth(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: (endpoint: string, data?: any) =>
    fetchWithAuth(endpoint, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: (endpoint: string, data?: any) =>
    fetchWithAuth(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),
};

export default api; 